import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState,
    SlateButton, Select,
} from "@/components/common/UI";
import { VarianceBadge, varianceLabel } from "@/components/zbc/VarianceBadge";
import { fmtINR, fmtDate, fmtPct } from "@/lib/format";
import { FileText, ArrowLeft, Factory, AlertTriangle } from "lucide-react";

export default function VarianceReport() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [error, setError] = useState("");
    const [filterBadge, setFilterBadge] = useState("ALL");
    const [filterCustomer, setFilterCustomer] = useState("ALL");

    const load = () =>
        Promise.all([api.get("/zbcs/variance-report"), api.get("/customers")])
            .then(([r, c]) => { setData(r.data); setCustomers(c.data); })
            .catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const customerMap = useMemo(() => Object.fromEntries(customers.map((c) => [c.id, c])), [customers]);
    const filtered = useMemo(() => {
        if (!data) return [];
        let rows = data.rows;
        if (filterBadge !== "ALL") rows = rows.filter((r) => r.variance_badge === filterBadge);
        if (filterCustomer !== "ALL") rows = rows.filter((r) => r.customer_id === filterCustomer);
        return rows;
    }, [data, filterBadge, filterCustomer]);

    return (
        <div data-testid="zbc-variance-report">
            <div className="mb-4">
                <Link to="/zbc" className="text-[12px] text-slate-500 hover:text-[hsl(var(--copper))] inline-flex items-center gap-1">
                    <ArrowLeft className="h-3 w-3" /> Back to ZBCs
                </Link>
            </div>
            <PageHeader
                eyebrow="ZBC · ANALYSIS"
                title="Variance Report"
                subtitle="Where the supplier is quoting above the should-cost, sorted by largest variance. Use these to prioritise negotiations."
            />

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && !data && <LoadingState />}

            {data && (
                <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                        <Card className="p-4">
                            <div className="eyebrow text-slate-500">TOTAL</div>
                            <div className="font-mono tabular-nums text-2xl font-semibold mt-1">{data.total}</div>
                            <div className="text-[11px] text-slate-500 mt-1">ZBCs with supplier quote</div>
                        </Card>
                        {["GREEN", "AMBER", "RED", "FLAG"].map((b) => (
                            <Card key={b} className="p-4">
                                <div className="eyebrow text-slate-500">{b} · {varianceLabel(b).toUpperCase()}</div>
                                <div className="flex items-baseline gap-2 mt-1">
                                    <span className="font-mono tabular-nums text-2xl font-semibold">{data.counts[b]}</span>
                                </div>
                                <div className="mt-2"><VarianceBadge badge={b} showPct={false} /></div>
                            </Card>
                        ))}
                    </div>

                    <Card className="p-3 mb-4">
                        <div className="flex flex-wrap gap-2">
                            <Select value={filterBadge} onChange={(e) => setFilterBadge(e.target.value)} className="w-48 h-8 text-[12px]">
                                <option value="ALL">All variance badges</option>
                                <option value="GREEN">Green &lt; 5%</option>
                                <option value="AMBER">Amber 5–15%</option>
                                <option value="RED">Red &gt; 15%</option>
                                <option value="FLAG">Flag (below cost)</option>
                            </Select>
                            <Select value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)} className="w-48 h-8 text-[12px]">
                                <option value="ALL">All customers</option>
                                {customers.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                            </Select>
                        </div>
                    </Card>

                    {filtered.length === 0 ? (
                        <EmptyState
                            icon={FileText}
                            title="No variances to report"
                            description="Either no ZBCs have supplier quotes yet or your filters are too restrictive."
                        />
                    ) : (
                        <Card>
                            <table className="w-full table-rowhover">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50/60">
                                        <th className="eyebrow text-left py-2 px-4">Part Number</th>
                                        <th className="eyebrow text-left py-2 px-4">Part Name</th>
                                        <th className="eyebrow text-left py-2 px-4">Customer</th>
                                        <th className="eyebrow text-right py-2 px-4">Should-Cost</th>
                                        <th className="eyebrow text-right py-2 px-4">Supplier ₹</th>
                                        <th className="eyebrow text-right py-2 px-4">Δ ₹</th>
                                        <th className="eyebrow text-left py-2 px-4">Variance</th>
                                        <th className="eyebrow text-left py-2 px-4">Quote Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((z) => (
                                        <tr key={z.id} className="border-b border-slate-100 cursor-pointer" onClick={() => navigate(`/zbc/${z.id}`)}>
                                            <td className="py-1.5 px-4 font-mono font-semibold text-[13px]">{z.part_number}</td>
                                            <td className="py-1.5 px-4 text-[13px] max-w-[200px] truncate">{z.part_name}</td>
                                            <td className="py-1.5 px-4 text-[12px] text-slate-600">{customerMap[z.customer_id]?.code || "—"}</td>
                                            <td className="py-1.5 px-4 text-right font-mono tabular-nums text-[13px]">{fmtINR(z.should_cost)}</td>
                                            <td className="py-1.5 px-4 text-right font-mono tabular-nums text-[13px] font-semibold">{fmtINR(z.supplier_quote)}</td>
                                            <td className={`py-1.5 px-4 text-right font-mono tabular-nums text-[13px] ${z.variance_amount >= 0 ? "text-red-600" : "text-emerald-600"}`}>
                                                {z.variance_amount >= 0 ? "+" : ""}{fmtINR(z.variance_amount)}
                                            </td>
                                            <td className="py-1.5 px-4"><VarianceBadge badge={z.variance_badge} pct={z.variance_pct} /></td>
                                            <td className="py-1.5 px-4 text-[11px] font-mono text-slate-500">{z.quote_date ? fmtDate(z.quote_date) : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Card>
                    )}

                    <CrossPlantBenchmark />
                </>
            )}
        </div>
    );
}

function CrossPlantBenchmark() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/zbcs/cross-plant-benchmark")
            .then((r) => setData(r.data))
            .catch((e) => setError(formatApiError(e)));
    }, []);

    if (error) return null; // non-critical section — fail quietly rather than block the page
    if (!data) return null;

    return (
        <Card className="mt-4">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                    <div className="eyebrow text-slate-500 mb-1 flex items-center gap-1.5"><Factory className="h-3 w-3" /> CROSS-PLANT BENCHMARK</div>
                    <p className="text-[12px] text-slate-500">
                        Average cost-per-kg by material category and plant, for VERIFIED ZBCs. Flags a plant whose average deviates &gt;{data.outlier_threshold_pct}% from the cross-plant median for that category.
                    </p>
                </div>
                {data.outlier_count > 0 && (
                    <div className="text-[12px] text-red-600 font-semibold flex items-center gap-1 shrink-0">
                        <AlertTriangle className="h-3.5 w-3.5" /> {data.outlier_count} outlier{data.outlier_count === 1 ? "" : "s"}
                    </div>
                )}
            </div>
            {data.cells.length === 0 ? (
                <div className="p-4 text-[12px] text-slate-500">Not enough VERIFIED ZBCs across plants yet to benchmark.</div>
            ) : (
                <table className="w-full text-[12.5px]">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50/60">
                            <th className="eyebrow text-left py-2 px-4">Category</th>
                            <th className="eyebrow text-left py-2 px-4">Plant</th>
                            <th className="eyebrow text-right py-2 px-4">ZBCs</th>
                            <th className="eyebrow text-right py-2 px-4">Avg ₹/kg</th>
                            <th className="eyebrow text-right py-2 px-4">Category Median</th>
                            <th className="eyebrow text-right py-2 px-4">Deviation</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.cells.map((c) => (
                            <tr key={`${c.rm_category}-${c.plant_id}`} className={`border-b border-slate-100 ${c.is_outlier ? "bg-red-50/40" : ""}`}>
                                <td className="py-2 px-4 font-mono text-[12px]">{c.rm_category}</td>
                                <td className="py-2 px-4">{c.plant_name || c.plant_code || c.plant_id}</td>
                                <td className="py-2 px-4 text-right font-mono tabular-nums">{c.zbc_count}</td>
                                <td className="py-2 px-4 text-right font-mono tabular-nums font-semibold">{fmtINR(c.avg_cost_per_kg, 2)}</td>
                                <td className="py-2 px-4 text-right font-mono tabular-nums text-slate-500">{fmtINR(c.median_cost_per_kg, 2)}</td>
                                <td className={`py-2 px-4 text-right font-mono tabular-nums ${c.is_outlier ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                                    {c.deviation_pct > 0 ? "+" : ""}{fmtPct(c.deviation_pct)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Card>
    );
}
